import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaMicroscope, FaArrowRight,
  FaCheckCircle, FaUsers, FaGraduationCap,
  FaBolt, FaWater, FaHandRock, FaBed, FaDna,
  FaSyringe, FaChartLine, FaSearch, FaStethoscope,
} from 'react-icons/fa';
import { MdPsychology } from 'react-icons/md';
import { GiBrain } from 'react-icons/gi';

const services = [
  {
    slug: 'stroke-cerebrovascular',
    icon: <GiBrain size={34} />,
    title: 'Stroke & Cerebrovascular',
    description: 'Acute stroke diagnosis, rapid thrombolysis therapy, and comprehensive post-stroke rehabilitation planning.',
    tags: ['Ischaemic Stroke', 'TIA', 'Thrombolysis'],
  },
  {
    slug: 'epilepsy-seizures',
    icon: <FaBolt size={30} />,
    title: 'Epilepsy & Seizures',
    description: 'Precise seizure classification, EEG-guided evaluation, and long-term anti-epileptic management.',
    tags: ['EEG Monitoring', 'AED Management', 'Seizure Control'],
  },
  {
    slug: 'movement-disorders',
    icon: <FaMicroscope size={30} />,
    title: 'Movement Disorders',
    description: "Parkinson's disease, essential tremors, dystonia diagnosis and advanced treatment protocols.",
    tags: ["Parkinson's", 'Tremors', 'Dystonia'],
  },
  {
    slug: 'memory-dementia',
    icon: <MdPsychology size={34} />,
    title: 'Memory & Dementia',
    description: "Alzheimer's disease diagnosis, cognitive assessment, and structured memory care planning.",
    tags: ["Alzheimer's", 'Vascular Dementia', 'MCI'],
  },
  {
    slug: 'headache-migraine',
    icon: <FaWater size={30} />,
    title: 'Headache & Migraine',
    description: 'Evidence-based chronic migraine and cluster headache treatment with preventive strategies.',
    tags: ['Migraine', 'Cluster Headache', 'Preventive Therapy'],
  },
  {
    slug: 'neuropathy',
    icon: <FaHandRock size={30} />,
    title: 'Neuropathy',
    description: 'Peripheral nerve disorder diagnosis, nerve conduction studies, and neuropathic pain management.',
    tags: ['Diabetic Neuropathy', 'NCS/EMG', 'Pain Management'],
  },
  {
    slug: 'sleep-disorders',
    icon: <FaBed size={30} />,
    title: 'Sleep Disorders',
    description: 'Narcolepsy, restless leg syndrome, REM sleep behaviour disorder — diagnosis and management.',
    tags: ['Narcolepsy', 'Restless Legs', 'REM Disorder'],
  },
  {
    slug: 'multiple-sclerosis',
    icon: <FaDna size={30} />,
    title: 'Multiple Sclerosis',
    description: 'MS diagnosis, disease-modifying therapy initiation, relapse management and long-term monitoring.',
    tags: ['RRMS', 'DMT', 'MRI Monitoring'],
  },
  {
    slug: 'neuro-infections',
    icon: <FaSyringe size={30} />,
    title: 'Neuro-infections',
    description: 'Meningitis, encephalitis, and CNS infection diagnosis, targeted treatment and follow-up.',
    tags: ['Meningitis', 'Encephalitis', 'TBM'],
  },
  {
    slug: 'eeg-evaluation',
    icon: <FaChartLine size={30} />,
    title: 'EEG Evaluation',
    description: 'In-clinic EEG for seizure monitoring, encephalopathy evaluation, and neurological workup.',
    tags: ['Routine EEG', 'Sleep EEG', 'Ambulatory EEG'],
  },
  {
    slug: 'ncs-emg',
    icon: <FaSearch size={30} />,
    title: 'NCS / EMG',
    description: 'Nerve conduction studies and EMG for neuropathy, radiculopathy, and neuromuscular disorders.',
    tags: ['Motor NCS', 'Sensory NCS', 'Needle EMG'],
  },
  {
    slug: 'neuro-critical-care',
    icon: <FaStethoscope size={30} />,
    title: 'Neuro-critical Care',
    description: 'Expert management of acute neurological emergencies — status epilepticus, GBS, encephalopathy.',
    tags: ['Status Epilepticus', 'GBS', 'ICU Neurology'],
  },
];

const Services = () => {
  const navigate = useNavigate();

  return (
    <div className="services-page">

      {/* ── PAGE HERO ── */}
      <section className="page-hero">
        <div className="page-hero-bg-dots" />
        <div className="container">
          <div className="page-hero-content">
            <div className="section-tag">What We Treat</div>
            <h1 className="page-hero-title">Neurology Services</h1>
            <p className="page-hero-sub">
              Comprehensive brain & nervous system care — diagnosis, treatment and long-term management of all neurological conditions.
            </p>
          </div>
        </div>
      </section>

      

      {/* ── SERVICES GRID ── */}
      <section className="services-main-section">
        <div className="container">
          <div className="services-full-grid">
            {services.map((s) => (
              <div key={s.title} className="service-card-full service-card-equal">
                <div className="service-card-icon-lg">{s.icon}</div>
                <h3 className="service-card-title">{s.title}</h3>
                <p className="service-card-desc">{s.description}</p>
                <div className="service-tags-row">
                  {s.tags.map(tag => (
                    <span key={tag} className="service-tag">{tag}</span>
                  ))}
                </div>
                <button
                  className="service-learn-more-btn"
                  onClick={() => navigate(`/services/${s.slug}`)}
                >
                  Learn More <FaArrowRight size={12} style={{ marginLeft: 6 }} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── IN-CLINIC DIAGNOSTICS ── */}
      <section className="services-diagnostics-section">
        <div className="container">
          <div className="diagnostics-highlight-card">
            <div className="diagnostics-highlight-icon"><FaMicroscope size={44} color="var(--teal)" /></div>
            <div className="diagnostics-highlight-text">
              <h3>Advanced In-Clinic Diagnostics</h3>
              <p>
                Naik Neuro Clinic offers EEG, Nerve Conduction Studies (NCS), and EMG — all in-house.
                No referrals, no delays. Results are personally interpreted by Dr. Naik for precise, same-visit diagnosis.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA BANNER ── */}
      <section className="cta-banner">
        <div className="container">
          <div className="cta-inner">
            <div className="cta-text">
              <h2 className="cta-title">Need Expert Neurological Care?</h2>
              <p className="cta-sub">Schedule a consultation with Dr. Rahul Naik — Available Mon–Sat, Morning & Evening.</p>
            </div>
            <div className="cta-actions">
              <button className="cta-btn-primary" onClick={() => navigate('/contact')}>Book Appointment</button>
              <button className="cta-btn-secondary" onClick={() => navigate('/about')}>About Dr. Naik</button>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Services;